import { foundationSubSystemTypeForPathAssembly } from "building-generation/elements/foundation/foundationAssemblyTools";
import {
  floorPathCoordinates,
  foundationPathCoordinates,
} from "building-generation/elements/foundation/pathCoordinates";
import {
  foundationConditionMatColor,
  foundationSectionMatColor,
  slabMatColor,
} from "building-generation/utils/foundationKeyMeasuresColor";
import { addAnalyticExtrusion, addAnalyticFace, addAnalyticLineSegment } from "building-generation/utils/generateUtil";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity, BuildingAnalyticEntityStyle } from "building-types/buildingAnalyticEntityTypes";
import type { BuildingGeomAnalytic, BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  buildingGeomAnalyticTagNew,
  buildingGeomAttributeTagNew,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { BuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import {
  FoundationComponentType,
  type CurbEntity,
  type FootingEntity,
  type PierEntity,
  type StemWallEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import type { FoundationSubSystemType } from "building-types/foundations/foundationEnums";
import type {
  GeneratedFoundationSuperSystem,
  GeneratedFoundationSubSystem,
  GeneratedFoundationHorizontalAssemblySubSystem,
  Slab,
} from "building-types/foundations/foundationTypes";
import {
  FOUNDATION_ATTRIBUTE_COMPONENT_TYPE,
  FOUNDATION_ATTRIBUTE_CONDITION,
  FOUNDATION_ATTRIBUTE_STEM_WALL_SUBSYSTEM_TYPE,
  type FoundationSection,
  type FoundationStrip,
} from "building-types/foundations/foundationTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { RWTable, Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { materialSrcFromPalette } from "doc/materialLookup";
import type { Line3Segment } from "geom/line3";
import { polyLine3New } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec2Normalize } from "geom/vec2";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

const FOUNDATION_TAG = buildingGeomAnalyticTagNew("FOUNDATION");
const SLAB_TAG = buildingGeomAnalyticTagNew("SLAB");
const FOUNDATION_EDGE_LINE_WIDTH = worldInchesNew(4);
const FOUNDATION_FACE_OPACITY = percentNew(0.3);

function slabStyle(slab: Slab): BuildingAnalyticEntityStyle {
  return {
    color: slabMatColor(slab).getHex(),
    opacity: FOUNDATION_FACE_OPACITY,
    lineWidth3d: FOUNDATION_EDGE_LINE_WIDTH,
  };
}

function sectionStyle(section: FoundationSection): BuildingAnalyticEntityStyle {
  return {
    color: foundationSectionMatColor(section).getHex(),
    opacity: FOUNDATION_FACE_OPACITY,
    lineWidth3d: FOUNDATION_EDGE_LINE_WIDTH,
  };
}

function stripStyle(strip: FoundationStrip): BuildingAnalyticEntityStyle {
  return {
    color: foundationConditionMatColor(strip.path.condition).getHex(),
    opacity: FOUNDATION_FACE_OPACITY,
    lineWidth3d: FOUNDATION_EDGE_LINE_WIDTH,
  };
}

function sectionComponentTypeAndCondition(section: FoundationSection): {
  componentType: FoundationComponentType;
  condition: string;
  type: string | undefined;
} {
  const componentType = section.size.sectionComponent.componentType;
  const condition = section.path.condition;
  let type: string | undefined;
  switch (componentType) {
    case FoundationComponentType.FOOTING: {
      const footing = section.size.sectionComponent as FootingEntity;
      type = footing.footingType;
      break;
    }
    case FoundationComponentType.STEMWALL: {
      const stemWall = section.size.sectionComponent as StemWallEntity;
      type = stemWall.stemWallType;
      break;
    }
    case FoundationComponentType.CURB: {
      const curb = section.size.sectionComponent as CurbEntity;
      type = curb.curbType;
      break;
    }
    case FoundationComponentType.PIER: {
      const pier = section.size.sectionComponent as PierEntity;
      type = pier.pierType;
      break;
    }
    default:
      assertUnreachable(componentType);
  }
  return { componentType, condition, type };
}

/**
 * Emit analytic extrusions for one foundation section. The building-query controller groups
 * collinear extrusions semantically (one row, multiple selects); generation does not merge geometry.
 */
function emitStripSectionExtrusion(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  exteriorPalette: Building["exteriorPalette"],
  handle: DocEntityHandle,
  baseCondition: BuildingGeomAnalytic,
  section: FoundationSection,
  sectionHandle: DocEntityHandle,
  len2Dir: Vec2,
  foundationSubSystemType: FoundationSubSystemType,
): void {
  const style = sectionStyle(section);
  const { type, componentType } = sectionComponentTypeAndCondition(section);
  const componentTypeTag = buildingGeomAttributeTagNew(
    FOUNDATION_ATTRIBUTE_COMPONENT_TYPE,
    section.size.sectionComponent.componentType,
    "STRING",
  );
  const conditionTag = buildingGeomAttributeTagNew(FOUNDATION_ATTRIBUTE_CONDITION, section.path.condition, "STRING");
  const componentCondition = buildingGeomAnalyticTagNew(section.size.sectionComponent.componentType);
  const tags: BuildingGeomTag[] = [FOUNDATION_TAG, baseCondition, componentTypeTag, conditionTag, componentCondition];
  if (componentType === FoundationComponentType.STEMWALL) {
    tags.push(
      buildingGeomAttributeTagNew(FOUNDATION_ATTRIBUTE_STEM_WALL_SUBSYSTEM_TYPE, foundationSubSystemType, "STRING"),
    );
  }
  for (const extrusion of section.extrusions) {
    addAnalyticExtrusion(
      entityType,
      entities,
      `${baseCondition.condition} ${componentTypeTag.attributeValue} ${conditionTag.attributeValue}`,
      [handle, sectionHandle],
      extrusion,
      materialSrcFromPalette(exteriorPalette, section.material),
      tags,
      style,
      type,
      len2Dir,
    );
  }
}

export function foundationSystemAnalyticEntities(
  building: ReturnType<typeof selectFoundation>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};
  const foundationSystem = building.foundations[buildingEntityHandle.id] as GeneratedFoundationSubSystem | undefined;
  if (!foundationSystem) {
    return entities;
  }

  const handle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };

  const { exteriorPalette, floorPaths, planarGraphs, doc } = building;
  const { subSystem } = foundationSystem;
  const foundationAssemblies = doc.foundationAssemblies ?? {};
  const levelFloorPaths = floorPaths[foundationSystem.levelId];

  if (!levelFloorPaths) {
    throw new Error(`Floor paths not found for level ${foundationSystem.levelId}`);
  }

  const baseCondition = buildingGeomAnalyticTagNew(subSystem.type);

  for (const strip of Object.values(subSystem.strips)) {
    const stripHandle: DocEntityHandle = {
      type: "GENERATED_ENTITY",
      entity: {
        entityType: "FOUNDATION_STRIP",
        id: strip.id,
      },
    };
    const floorPath = levelFloorPaths[strip.path.halfEdgeIndex];
    if (!floorPath) {
      continue;
    }
    const [startX, startY, endX, endY] = floorPathCoordinates(floorPath, planarGraphs);
    const pathSegment3: Line3Segment = polyLine3New([
      startX,
      startY,
      floorPath.floorOffset,
      endX,
      endY,
      floorPath.floorOffset,
    ]) as unknown as Line3Segment;

    const [len2DirX, len2DirY] = vec2Normalize(endX - startX, endY - startY);
    const len2Dir: Vec2 = [len2DirX, len2DirY];

    addAnalyticLineSegment(
      "ANALYTIC_FOUNDATION",
      entities,
      strip.path.condition,
      [handle, stripHandle],
      pathSegment3,
      [
        FOUNDATION_TAG,
        buildingGeomAnalyticTagNew(strip.path.condition),
        buildingGeomAttributeTagNew(FOUNDATION_ATTRIBUTE_CONDITION, strip.path.condition, "STRING"),
      ],
      stripStyle(strip),
    );

    for (const section of strip.sections) {
      const sectionHandle: DocEntityHandle = {
        type: "GENERATED_ENTITY",
        entity: {
          entityType: "FOUNDATION_SECTION",
          id: section.id,
        },
      };
      emitStripSectionExtrusion(
        "ANALYTIC_FOUNDATION",
        entities,
        exteriorPalette,
        handle,
        baseCondition,
        section,
        sectionHandle,
        len2Dir,
        foundationSubSystemTypeForPathAssembly(strip.path, foundationAssemblies, subSystem.type),
      );
    }
  }

  return entities;
}

/**
 * Room-based foundations merged into {@link Building.foundations} during overlap resolution.
 * Horizontal assemblies are emitted separately via {@link foundationHorizontalAssemblyAnalyticEntities}.
 */
export function addRoomBasedFoundationSystemAnalyticEntities(
  building: ReturnType<typeof selectFoundation>,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};
  for (const foundationId in building.foundations) {
    if (building.foundationHorizontalAssemblies[foundationId]) {
      continue;
    }
    Object.assign(
      entities,
      foundationSystemAnalyticEntities(building, { entityType: "FOUNDATION_SYSTEM", id: foundationId }),
    );
  }
  return entities;
}

export function foundationSuperSystemAnalyticEntities(
  building: ReturnType<typeof selectFoundationSuperSystem>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};
  const foundationSystem = building.foundationSuperSystems[buildingEntityHandle.id] as
    | GeneratedFoundationSuperSystem
    | undefined;
  if (!foundationSystem) {
    return entities;
  }
  const { superSystem } = foundationSystem;

  const handle: GeneratedEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };

  for (const slab of superSystem.slabs) {
    const { extrusion, roomClass, id } = slab;
    const slabHandle: GeneratedEntityHandle = {
      type: "GENERATED_ENTITY",
      entity: {
        entityType: "SLAB",
        id,
      },
    };
    const style = slabStyle(slab);
    const tags = [
      buildingGeomAnalyticTagNew(RoomClass[roomClass]),
      FOUNDATION_TAG,
      SLAB_TAG,
      buildingGeomAnalyticTagNew(superSystem.type),
      buildingGeomAttributeTagNew("roomClass", RoomClass[slab.roomClass], "STRING"),
    ];
    addAnalyticFace(
      "ANALYTIC_FOUNDATION_SUPER_SYSTEM",
      entities,
      superSystem.type + " top face",
      [handle, slabHandle],
      extrusion.profile,
      undefined,
      tags,
      style,
    );
    addAnalyticExtrusion(
      "ANALYTIC_FOUNDATION_SUPER_SYSTEM",
      entities,
      superSystem.type,
      [handle, slabHandle],
      extrusion,
      undefined,
      tags,
      style,
    );
  }

  return entities;
}

export function foundationHorizontalAssemblyAnalyticEntities(
  building: ReturnType<typeof selectFoundationHorizontalAssembly>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};
  const foundationSystem = building.foundationHorizontalAssemblies[buildingEntityHandle.id] as
    | GeneratedFoundationHorizontalAssemblySubSystem
    | undefined;
  if (!foundationSystem) {
    return entities;
  }

  const handle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };

  const { foundationHorizontalAssembliesPaths, planarGraphs, levels, foundations, doc } = building;
  const overlapResolvedFoundation = foundations[buildingEntityHandle.id];
  const subSystem = overlapResolvedFoundation?.subSystem ?? foundationSystem.subSystem;
  const foundationPaths = foundationHorizontalAssembliesPaths[buildingEntityHandle.id];
  const foundationAssemblies = doc.foundationAssemblies ?? {};

  if (!foundationPaths) {
    return entities;
  }

  const floorOffset = levels[foundationSystem.levelId]?.floorElevation ?? 0;
  const baseCondition = buildingGeomAnalyticTagNew(subSystem.type);

  for (const strip of Object.values(subSystem.strips)) {
    const stripHandle: DocEntityHandle = {
      type: "GENERATED_ENTITY",
      entity: {
        entityType: "FOUNDATION_STRIP",
        id: strip.id,
      },
    };
    const foundationPath = foundationPaths[strip.path.halfEdgeIndex];
    if (!foundationPath) {
      continue;
    }
    const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
    const pathSegment3: Line3Segment = polyLine3New([
      startX,
      startY,
      floorOffset,
      endX,
      endY,
      floorOffset,
    ]) as unknown as Line3Segment;

    const [len2DirX, len2DirY] = vec2Normalize(endX - startX, endY - startY);
    const len2Dir: Vec2 = [len2DirX, len2DirY];

    addAnalyticLineSegment(
      "ANALYTIC_FOUNDATION_HORIZONTAL_ASSEMBLY",
      entities,
      strip.path.condition,
      [handle, stripHandle],
      pathSegment3,
      [
        FOUNDATION_TAG,
        buildingGeomAnalyticTagNew(strip.path.condition),
        buildingGeomAttributeTagNew(FOUNDATION_ATTRIBUTE_CONDITION, strip.path.condition, "STRING"),
      ],
      stripStyle(strip),
    );

    for (const section of strip.sections) {
      const sectionHandle: DocEntityHandle = {
        type: "GENERATED_ENTITY",
        entity: {
          entityType: "FOUNDATION_SECTION",
          id: section.id,
        },
      };
      emitStripSectionExtrusion(
        "ANALYTIC_FOUNDATION_HORIZONTAL_ASSEMBLY",
        entities,
        building.exteriorPalette,
        handle,
        baseCondition,
        section,
        sectionHandle,
        len2Dir,
        foundationSubSystemTypeForPathAssembly(foundationPath, foundationAssemblies, subSystem.type),
      );
    }
  }

  return entities;
}

const selectFoundation = makeBuildingSelector([
  "foundations",
  "foundationHorizontalAssemblies",
  "exteriorPalette",
  "floorPaths",
  "planarGraphs",
  "doc",
]);

function generateFoundation(
  building: ReturnType<typeof selectFoundation>,
): Pick<Building, "analyticFoundationEntities"> {
  return { analyticFoundationEntities: addRoomBasedFoundationSystemAnalyticEntities(building) };
}

export const generateFoundationAnalyticEntities = memo(selectFoundation, generateFoundation);

const selectFoundationSuperSystem = makeBuildingSelector(["foundationSuperSystems"]);

function generateFoundationSuperSystem(
  building: ReturnType<typeof selectFoundationSuperSystem>,
): Pick<Building, "analyticFoundationSuperSystemEntities"> {
  const analyticFoundationSuperSystemEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of Object.keys(building.foundationSuperSystems)) {
    Object.assign(
      analyticFoundationSuperSystemEntities,
      foundationSuperSystemAnalyticEntities(building, { entityType: "FOUNDATION_SUPER_SYSTEM", id }),
    );
  }
  return { analyticFoundationSuperSystemEntities };
}

export const generateFoundationSuperSystemAnalyticEntities = memo(
  selectFoundationSuperSystem,
  generateFoundationSuperSystem,
);

const selectFoundationHorizontalAssembly = makeBuildingSelector([
  "foundationHorizontalAssemblies",
  "foundationHorizontalAssembliesPaths",
  "foundations",
  "planarGraphs",
  "levels",
  "doc",
  "exteriorPalette",
]);

function generateFoundationHorizontalAssembly(
  building: ReturnType<typeof selectFoundationHorizontalAssembly>,
): Pick<Building, "analyticFoundationHorizontalAssemblyEntities"> {
  const analyticFoundationHorizontalAssemblyEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of Object.keys(building.foundationHorizontalAssemblies)) {
    Object.assign(
      analyticFoundationHorizontalAssemblyEntities,
      foundationHorizontalAssemblyAnalyticEntities(building, {
        entityType: "FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY",
        id,
      }),
    );
  }
  return { analyticFoundationHorizontalAssemblyEntities };
}

export const generateFoundationHorizontalAssemblyAnalyticEntities = memo(
  selectFoundationHorizontalAssembly,
  generateFoundationHorizontalAssembly,
);
